import React, { useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import toast from "react-hot-toast";

import { formatDisplayDate } from "../../utils/format";
import { BrandedQrCode } from "./BrandedQrCode";
import "./EventInviteQrCard.css";

type Props = {
  url: string;
  qrId: string;
  eventName?: string;
  eventDate?: string;
  venue?: string;
};

export const EventInviteQrCard: React.FC<Props> = ({ url, qrId, eventName, eventDate, venue }) => {
  const posterRef = useRef<HTMLDivElement>(null);
  const posterQrId = `${qrId}-poster`;

  const handleDownload = async () => {
    const node = posterRef.current;
    if (!node) return;
    try {
      const canvas = await html2canvas(node, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      });
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error("empty canvas");
      }
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: canvas.width >= canvas.height ? "landscape" : "portrait",
        unit: "px",
        format: [canvas.width, canvas.height],
        compress: true,
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save("event-qr.pdf");
    } catch {
      toast.error("Could not download QR");
    }
  };

  return (
    <div className="event-invite-qr" aria-label="Event QR">
      {url ? (
        <>
          <div className="event-invite-qr__qr">
            <BrandedQrCode id={qrId} value={url} variant="svg" theme="light" size={180} />
          </div>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="event-invite-qr__open"
            title="Open the form this QR points to"
            aria-label="Open the form this QR points to"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 4h6v6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 14 20 4" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18 14v6H4V6h6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          <button
            type="button"
            className="event-invite-qr__download"
            title="Download QR"
            aria-label="Download QR"
            onClick={() => void handleDownload()}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3v12m0 0-4-4m4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 19h14" strokeLinecap="round" />
            </svg>
          </button>

          {/* Off-screen poster used only as the html2canvas source for the PDF download. */}
          <div className="event-qr-poster" ref={posterRef} aria-hidden="true">
            <div className="event-qr-poster__top">
              <div>
                <div className="event-qr-poster__event-name">{eventName || "Event Name"}</div>
                <div className="event-qr-poster__venue">{venue || "Venue"}</div>
              </div>
              <div className="event-qr-poster__date">{eventDate ? formatDisplayDate(eventDate) : "Date"}</div>
            </div>
            <div className="event-qr-poster__cta">
              <div className="event-qr-poster__headline">Didn&apos;t RSVP? Bold move.</div>
              <div className="event-qr-poster__subheadline">Scan here!</div>
            </div>
            <div className="event-qr-poster__qr-frame">
              <BrandedQrCode id={posterQrId} value={url} variant="svg" theme="light" size={210} />
            </div>
          </div>
        </>
      ) : (
        <div className="eventos-empty">Generating…</div>
      )}
    </div>
  );
};
